// Complaint Call Logging Register — unified Maintenance + Complaint register
// (Annexure-2, Format No: IKM/TSIM/FMT/19)
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  useRegisterList, RegisterHeader, SearchBox, RegisterPrintActions,
  RegisterTable, RegisterEntryDialog, AddEntryButton, GovFooterNote, fetchBundle, useLocalBodyName,
} from "./shared";
import { sectionsForOption } from "@/lib/registerPdf";

const EMPTY = {
  hardware_id: "", reporting_date: "", complaint_remarks: "", service_date: "",
  solved: "", service_remarks: "", technician_contact: "", tracking_number: "",
};

const FIELDS = [
  { name: "hardware_id", label: "Linked Hardware Asset", type: "asset", full: true },
  { name: "reporting_date", label: "Reporting Date", type: "date" },
  { name: "service_date", label: "Service Date", type: "date" },
  { name: "complaint_remarks", label: "Complaint Remarks", type: "textarea" },
  { name: "service_remarks", label: "Service Remarks", type: "textarea" },
  { name: "solved", label: "Solved Status", type: "select", options: ["Yes", "No"] },
  { name: "tracking_number", label: "Tracking Number" },
  { name: "technician_contact", label: "Name of Technician & Contact Number", full: true },
];

export default function ComplaintRegisterTab() {
  const { rows, loading, save, remove } = useRegisterList("complaints");
  const localBody = useLocalBodyName();
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/hardware").then(r => setAssets(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => [r.asset_id, r.comp_id, r.complaint_remarks, r.technician_contact, r.tracking_number, r.asset_type]
      .some(v => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="complaint-register-tab">
      <RegisterHeader
        title="COMPLAINT CALL LOGGING REGISTER"
        formatNo="IKM/TSIM/FMT/19"
        localBody={localBody}
        subtitle="Unified Maintenance + Complaint Register (Annexure-2)"
        testid="complaint-register"
      >
        <SearchBox value={search} onChange={setSearch} placeholder="Search asset, complaint ID, technician…" testid="complaint-search" />
        <RegisterPrintActions sections={sectionsForOption("complaints")} getBundle={fetchBundle} filename="Complaint_Call_Logging_Register.pdf" testid="complaint-print" />
        <AddEntryButton onClick={() => setEditing({ ...EMPTY })} label="Log Complaint" testid="complaint-add-btn" />
      </RegisterHeader>

      <RegisterTable
        testid="complaint-table"
        loading={loading}
        rows={filtered}
        emptyText="No complaints logged yet."
        columns={[
          { key: "sl_no", label: "Sl.", render: (r, i) => r.sl_no || i + 1 },
          { key: "asset_id", label: "Asset ID", render: r => <span className="font-mono text-xs">{r.asset_id || "—"}</span> },
          { key: "comp_id", label: "Comp. ID", render: r => <span className="font-mono text-xs">{r.comp_id}</span> },
          { key: "reporting_date", label: "Reported" },
          { key: "complaint_remarks", label: "Complaint" },
          { key: "solved", label: "Solved", render: r => r.solved === "Yes"
              ? <span className="text-emerald-700 font-semibold">Yes</span>
              : r.solved === "No" ? <span className="text-red-600 font-semibold">No</span> : "—" },
          { key: "service_date", label: "Service Date" },
          { key: "technician_contact", label: "Technician" },
        ]}
        onEdit={r => setEditing({ ...EMPTY, ...r })}
        onDelete={async r => { if (window.confirm(`Delete complaint ${r.comp_id}?`)) await remove(r); }}
      />
      <GovFooterNote />

      <RegisterEntryDialog
        open={!!editing}
        title={editing?.id ? `Edit Complaint ${editing.comp_id || ""}` : "Log New Complaint"}
        description="Complaint ID is auto-generated. Link the hardware asset to pull its details into the register."
        fields={FIELDS}
        values={editing || EMPTY}
        onChange={setEditing}
        onClose={() => setEditing(null)}
        onSave={async () => { if (await save(editing, editing?.id ? editing : null)) setEditing(null); }}
        assets={assets}
        testid="complaint-dialog"
      />
    </div>
  );
}
